// Helpers for @-mentions inside plan comments.
//
// A mention is stored inline in the comment body as a marker token
// "@[Full Name](uuid)". The author composes with plain "@Full Name" text and a
// tracked list of picked friends; on send we encode those into markers. When
// rendering, we parse the markers back into linkable spans. The server parses
// the same markers to notify each tagged friend, so the body is the single
// source of truth for who was mentioned.

export type Mention = {
  id: string;
  name: string;
};

export type MentionCandidate = {
  id: string;
  full_name: string;
  avatar_path?: string | null;
};

// 8-4-4-4-12 hex UUID. Kept in sync with the server-side regex.
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const MENTION_MARKER = new RegExp(`@\\[([^\\]]+)\\]\\((${UUID})\\)`, "g");

export type CommentSegment =
  | { type: "text"; value: string }
  | { type: "mention"; id: string; name: string };

// Split a stored comment body into plain-text and mention segments for render.
export function parseCommentSegments(body: string): CommentSegment[] {
  const segments: CommentSegment[] = [];
  let lastIndex = 0;
  MENTION_MARKER.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_MARKER.exec(body)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: body.slice(lastIndex, match.index) });
    }
    segments.push({ type: "mention", name: match[1], id: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ type: "text", value: body.slice(lastIndex) });
  }
  return segments;
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && /[\p{L}\p{N}_]/u.test(char);
}

// Replace each occurrence of "@<name>" that ends on a word boundary with the
// marker token. Longest names are processed first so a longer name is encoded
// before a shorter one that is a prefix of it (e.g. "Anna Berg" before "Anna").
export function encodeCommentBody(text: string, mentions: Mention[]): string {
  const ordered = mentions
    .filter((mention) => mention.name.length > 0)
    .sort((a, b) => b.name.length - a.name.length);

  let body = text;
  for (const mention of ordered) {
    const token = `@${mention.name}`;
    const marker = `@[${mention.name}](${mention.id})`;
    let result = "";
    let index = 0;
    while (index < body.length) {
      if (body.startsWith(token, index) && !isWordChar(body[index + token.length])) {
        result += marker;
        index += token.length;
      } else {
        result += body[index];
        index += 1;
      }
    }
    body = result;
  }
  return body;
}

// Locate an in-progress mention at the caret: the most recent "@" that begins a
// word (start of text or after whitespace) with no newline before the caret.
// Returns the "@" position and the query typed after it, or null. The query may
// contain spaces so multi-word names ("Anna Berg") can be matched as they type.
export function findActiveMention(
  text: string,
  caret: number
): { start: number; query: string } | null {
  const upToCaret = text.slice(0, caret);
  const at = upToCaret.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(upToCaret[at - 1])) return null;
  const query = upToCaret.slice(at + 1);
  if (query.length > 40 || /[\n\r]/.test(query)) return null;
  return { start: at, query };
}

// A friend matches the query when the query is empty, or is a case-insensitive
// prefix of the full name or of any word within it.
export function friendMatchesQuery(fullName: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  const name = fullName.toLowerCase();
  if (name.startsWith(needle)) return true;
  return name.split(/\s+/).some((word) => word.startsWith(needle));
}

export function matchFriends<T extends MentionCandidate>(
  friends: T[],
  query: string,
  limit = 6
): T[] {
  return friends.filter((friend) => friendMatchesQuery(friend.full_name, query)).slice(0, limit);
}
